import { useMemo } from 'react';
import { Flex, Text } from '@mantine/core';
import type { DealEventTct, SessionInfo } from '@dealstack/shared/lib/backendClient';
import { DATE_FORMATS, formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date.ts';
import { splitFullName } from '@dealstack/shared/lib/format/user';
import { AvatarInfoRow } from '@dealstack/frontend/shared/components/AvatarInfoRow/AvatarInfoRow';
import { SessionDetails } from '@dealstack/frontend/app/pages/Project/MIP/Messages/components/SessionDetails/SessionDetails';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { formatAuditSessionTitle } from '@dealstack/frontend/shared/components/Audit/AuditLayout/utils.ts';
import { getDealEventChanges } from '@dealstack/frontend/app/pages/Project/DealEventLog/utils/dealEventLogChanges.utils.ts';
import { formatDealEventHeading } from '@dealstack/frontend/app/pages/Project/DealEventLog/dealEventLog.utils.ts';
import { DealEventChangesTable } from '@dealstack/frontend/app/pages/Project/DealEventLog/components/DealEventLogTable/DealEventChangesTable.tsx';
import { DetailPanelLayout } from '@dealstack/frontend/app/pages/Project/DealEventLog/components/DealEventLogTable/DealEventDetailPanel.styles.tsx';

interface Props {
	event: DealEventTct;
	session?: SessionInfo;
}

const SessionPanel = ({ event, session }: { event: DealEventTct; session: SessionInfo }) => {
	const tCapManage = useTranslationCapManage();
	const { firstName, lastName } = splitFullName(session.fullName);

	return (
		<div className="deal-event-detail-session">
			<Flex direction="column" gap="0.5rem">
				<Text fz={14} c="gray.9">
					{formatAuditSessionTitle(event.action, tCapManage)}
				</Text>

				<AvatarInfoRow
					avatarSize="md"
					firstName={firstName}
					lastName={lastName}
					text={session.fullName}
					subText={`(${session.email})`}
				/>
			</Flex>

			<SessionDetails
				mode="simple"
				bgColor="gray.0"
				fullName={session.fullName}
				email={session.email}
				startDateAndTime={session.createdAt}
				ipAddress={session.ipAddress}
				location={session.location}
				deviceInfo={session.deviceInfo}
				isLoading={false}
			/>
		</div>
	);
};

export const DealEventDetailPanel = ({ event, session }: Props) => {
	const tCapManage = useTranslationCapManage();
	const tCommonUi = useTranslationCommonUi();
	const changes = useMemo(
		() => getDealEventChanges(event, tCapManage, tCommonUi),
		[event, tCapManage, tCommonUi]
	);

	return (
		<DetailPanelLayout>
			<div className="deal-event-detail-header">
				<Text fz={14} fw={600} c="dark.8">
					{formatDealEventHeading(event, tCapManage)}
				</Text>

				<Text fz={12} c="gray.9">
					{`${formatStringDateToUI(event.occurredAt, DATE_FORMATS.DATE_TIME_ALT)} · ${tCapManage(
						'deal_event_log.detail.state_changes',
						{ count: changes.length }
					)}`}
				</Text>
			</div>

			<Flex gap="0.75rem" align="flex-start" className="deal-event-detail-content">
				{changes.length > 0 && (
					<div className="deal-event-table">
						<DealEventChangesTable event={event} changes={changes} />
					</div>
				)}

				{!!session && <SessionPanel event={event} session={session} />}
			</Flex>
		</DetailPanelLayout>
	);
};
